import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, Col, Container, Row, Button, Badge, Spinner, Input } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import { Btn, H3 } from "../../../AbstractElements";
import { Plus, Eye, Check, Trash2, Edit, Tool, Layers, Settings } from "react-feather";
import { useNavigate } from "react-router-dom";
import { apiPost } from "../../../Utils";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import { useAuth } from "../../../Context/AuthContext";

const LPOList = () => {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState("");
    const [sortDirection, setSortDirection] = useState("");
    const [refresh, setRefresh] = useState(0);
    const prevSearch = useRef(searchTerm);
    useEffect(() => {
        const isSearchChanged = prevSearch.current !== searchTerm;
        const delay = isSearchChanged ? 500 : 0;
        prevSearch.current = searchTerm;

        const timer = setTimeout(() => {
            fetchData(currentPage, perPage, searchTerm, sortColumn, sortDirection);
        }, delay);
        return () => clearTimeout(timer);
    }, [currentPage, perPage, searchTerm, refresh, sortColumn, sortDirection]);

    const fetchData = async (page: number, size: number, search: string = "", sortCol: string = "", sortDir: string = "") => {
        setLoading(true);
        try {
            const body: any = {
                page: page,
                per_page: size,
                search: search
            };

            if (sortCol) {
                body.sort_by = sortCol;
                body.sort_direction = sortDir;
            }

            const response: any = await apiPost("lpos/getall", body);
            if (response && response.data) {
                setData(response.data);
                setTotalRows(response.total);
            }
        } catch (error) {
            console.error("Error fetching LPOs:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (column: any, sortDir: string) => {
        if (column.sortField) {
            setSortColumn(column.sortField);
            setSortDirection(sortDir);
            setCurrentPage(1);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleApprove = async (id: number) => {
        Swal.fire({
            title: 'Approve LPO?',
            text: "This will finalize the LPO.",
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Approve',
            confirmButtonColor: '#28a745'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost("lpos/approve", { id });
                    toast.success("LPO Approved");
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error("Approval Failed");
                }
            }
        });
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Delete LPO?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost("lpos/delete", { id });
                    toast.success("LPO Deleted");
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error("Failed to delete LPO");
                }
            }
        });
    };

    const columns = [
        {
            name: "Sl.No",
            selector: (row: any, index?: number) => (currentPage - 1) * perPage + (index || 0) + 1,
            sortable: false
        },
        {
            name: "LPO No.",
            selector: (row: any) => row.lpo_number,
            sortable: true,
            sortField: "lpo_number",
            wrap: true,
        },
        {
            name: "Branch",
            selector: (row: any) => row.branch?.branch_name || 'N/A',
            sortable: true,
            sortField: "branch_name",
            wrap: true,
        },
        {
            name: "Vehicle",
            selector: (row: any) => row.vehicle?.registration_number || 'N/A',
            sortable: true,
            sortField: "registration_number",
            wrap: true,
        },
        {
            name: "Date",
            selector: (row: any) => row.lpo_date,
            sortable: true,
            sortField: "lpo_date",
            wrap: true,
        },
        {
            name: "Vendor",
            selector: (row: any) => row.vendor_name,
            sortable: true,
            sortField: "vendor_name",
            wrap: true,
            cell: (row: any) => {
                const type = row.lpo_type;
                const pVendor = row.vendor_name;
                const lVendor = row.labour_vendor_name || row.vendor_name;

                const style = { textAlign: 'left' as const, width: '100%' };
                if (type === 'Same Hybrid') {
                    return <div style={style}><strong>Parts & Labour:</strong> {pVendor}</div>;
                } else if (type === 'Diff Hybrid') {
                    return (
                        <div
                            className="d-flex flex-column"
                            style={{
                                textAlign: 'left',
                                alignItems: 'flex-start',
                                width: '100%'
                            }}
                        >
                            <span className="mb-1">
                                <strong>Parts:</strong> {pVendor}
                            </span>
                            <span>
                                <strong>Labour:</strong> {lVendor}
                            </span>
                        </div>
                    );
                } else if (type === 'Labour') {
                    return <div style={style}><strong>Labour:</strong> {lVendor}</div>;
                } else {
                    return <div style={style}><strong>Parts:</strong> {pVendor}</div>;
                }
            }
        },
        {
            name: "Amount",
            selector: (row: any) => row.amount,
            sortable: true,
            sortField: "amount",
            cell: (row: any) => <span>{row.amount}</span>
        },
        {
            name: "Status",
            selector: (row: any) => row.status,
            sortable: true,
            sortField: "status",
            cell: (row: any) => (
                <Badge color={row.status === 'Approved' ? 'success' : row.status === 'Pending' ? 'warning' : 'secondary'}>
                    {row.status}
                </Badge>
            )
        },
        {
            width: "220px",
            cell: (row: any) => {
                const type = row.lpo_type;

                return (
                    <div className="d-flex gap-2">

                        {hasPermission('lpo.invoice') && (
                            <>
                                {/* Hybrid Same Vendor */}
                                {type === 'Same Hybrid' && (
                                    <Button size="sm" color="primary" onClick={() => navigate(`${process.env.PUBLIC_URL}/view_lpo`, { state: { id: row.id, viewMode: 'both' } })} title="View Hybrid LPO">
                                        <Layers size={14} />
                                    </Button>
                                )}

                                {/* Hybrid Diff Vendor */}
                                {type === 'Diff Hybrid' && (
                                    <>
                                        <Button size="sm" color="info" onClick={() => navigate(`${process.env.PUBLIC_URL}/view_lpo`, { state: { id: row.id, viewMode: 'parts' } })} title="View Parts">
                                            <Settings size={14} />
                                        </Button>
                                        <Button size="sm" color="warning" onClick={() => navigate(`${process.env.PUBLIC_URL}/view_lpo`, { state: { id: row.id, viewMode: 'labour' } })} title="View Labour">
                                            <Tool size={14} />
                                        </Button>
                                    </>
                                )}

                                {/* Single Part */}
                                {(type === 'Parts' || !type) && (
                                    <Button size="sm" color="info" onClick={() => navigate(`${process.env.PUBLIC_URL}/view_lpo`, { state: { id: row.id, viewMode: 'parts' } })} title="View Parts">
                                        <Settings size={14} />
                                    </Button>
                                )}

                                {/* Single Labour */}
                                {type === 'Labour' && (
                                    <Button size="sm" color="warning" onClick={() => navigate(`${process.env.PUBLIC_URL}/view_lpo`, { state: { id: row.id, viewMode: 'labour' } })} title="View Labour">
                                        <Tool size={14} />
                                    </Button>
                                )}
                            </>
                        )}


                        {row.status === 'Pending' && (
                            <>

                                <Button size="sm" color="warning" onClick={() => navigate(`${process.env.PUBLIC_URL}/edit_lpo`, { state: { id: row.id } })} title="Edit">
                                    <Edit size={14} />
                                </Button>
                                {hasPermission('lpo.approve') && (
                                    <Button size="sm" color="success" onClick={() => handleApprove(row.id)} title="Approve">
                                        <Check size={14} />
                                    </Button>
                                )}
                                {hasPermission('lpo.delete') && (
                                    <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete">
                                        <Trash2 size={14} />
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                );
            }
        }
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: {
                fontSize: '15px',
                fontWeight: '600',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    return (
        <div className="page-body">
            <Breadcrumbs parent="Operations" title="LPO Management" pageTitle="LPO List" />
            <Container fluid>
                <Row>
                    <Col sm={12}>
                        <Card>
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <H3>LPO Overview</H3>
                                    {hasPermission('lpo.add') && (
                                        <Btn color="primary" onClick={() => navigate(`${process.env.PUBLIC_URL}/add_lpo`)}>
                                            <Plus className="me-2" size={16} />Create LPO
                                        </Btn>
                                    )}
                                </div>
                                {hasPermission('lpo.view') && (
                                    <>
                                        <div className="d-flex justify-content-end mb-3">
                                            <Input
                                                type="text"
                                                placeholder="Search..."
                                                className="w-25"
                                                value={searchTerm}
                                                onChange={handleSearchChange}
                                            />
                                        </div>
                                        <DataTable
                                            columns={columns}
                                            data={data}
                                            pagination
                                            paginationServer
                                            paginationTotalRows={totalRows}
                                            onChangePage={handlePageChange}
                                            onChangeRowsPerPage={handlePerRowsChange}
                                            onSort={handleSort}
                                            sortServer
                                            progressPending={loading}
                                            progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                            customStyles={customStyles}
                                        />
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <ToastContainer />

        </div>
    );
};

export default LPOList;
